/**
 * Turning a pasted link into something that plays.
 *
 * Owners paste whatever the share button gave them, which for one platform is
 * four different URL shapes. Parsing them here means a video block shows a
 * player rather than a link out, and it means an unrecognised link degrades to
 * a link rather than to an empty frame.
 *
 * Only the platforms whose embed URL is a documented, stable, key-free shape.
 * Anything needing an API key or a lookup does not belong in a render path.
 */

export interface Embed {
  src: string
  title: string
  /**
   * Video fills whatever it is given. Audio has a floor rather than a fixed
   * height: Spotify picks its layout from the space it has, showing a bar at
   * eighty pixels and full artwork past about three hundred, so pinning it to
   * one height threw away the taller layout and left the rest of the card empty.
   */
  aspect: 'video' | 'audio'
  /**
   * The heights an audio player actually draws, tallest first, for a player
   * that picks a fixed layout from the room it has. Absent for one that redraws
   * at any height, which is then given all the room there is.
   */
  layouts?: number[]
}

/**
 * The layouts Spotify's player draws, tallest first.
 *
 * Handed any other height it renders the next one down and leaves the rest as
 * its own white page background, which is the block showing a white slab under
 * the player. So the frame is snapped to one of these rather than stretched.
 *
 * Only three exist, and the gaps between them are large. A two-row card has
 * roughly 296px of room and the next layout up is 352, so it falls to 152 and
 * leaves 144px of card doing nothing. That leftover is the thing to design
 * around rather than to fight: see `AudioFrame`.
 */
const SPOTIFY_LAYOUTS = [352, 152, 80]

function idFrom(pattern: RegExp, url: string): string | null {
  const match = url.match(pattern)
  return match?.[1] ?? null
}

/** youtube.com/watch?v=, youtu.be/, /embed/, /shorts/, /live/. */
function youtube(url: string): string | null {
  return idFrom(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/, url)
}

function vimeo(url: string): string | null {
  return idFrom(/vimeo\.com\/(?:video\/)?(\d+)/, url)
}

/**
 * tiktok.com/@user/video/{id}, /embed/v2/, /player/v1/, m.tiktok.com/v/.
 *
 * The username is allowed to be empty: a `vt.tiktok.com` share link, which the
 * backend expands on save, lands on `tiktok.com/@/video/{id}`.
 */
function tiktok(url: string): string | null {
  return idFrom(/tiktok\.com\/(?:@[\w.-]*\/video\/|embed\/(?:v2\/)?|player\/v1\/|v\/)(\d{8,})/, url)
}

/** instagram.com/reel/{code}, /reels/, /p/, /tv/, with or without a username in front. */
function instagram(url: string): { kind: 'reel' | 'p', code: string } | null {
  const match = url.match(/instagram\.com\/(?:[\w.]+\/)?(reels?|p|tv)\/([\w-]+)/)
  if(!match) return null
  return { kind: match[1] === 'p' ? 'p' : 'reel', code: match[2] }
}

/** open.spotify.com/{track|album|playlist|artist|episode|show}/{id}. */
function spotify(url: string): { kind: string, id: string } | null {
  const match = url.match(/open\.spotify\.com\/(?:intl-[a-z]{2}\/)?(track|album|playlist|artist|episode|show)\/([\w]+)/)
  return match ? { kind: match[1], id: match[2] } : null
}

export function videoEmbed(url: string | undefined, title: string): Embed | null {
  if(!url) return null

  const yt = youtube(url)
  // `rel=0` keeps the end screen inside the uploader's own videos, and
  // youtube-nocookie is the domain that does not set a tracking cookie until
  // the visitor actually presses play.
  if(yt) return { src: `https://www.youtube-nocookie.com/embed/${yt}?rel=0&autoplay=1`, title, aspect: 'video' }

  const vm = vimeo(url)
  if(vm) return { src: `https://player.vimeo.com/video/${vm}`, title, aspect: 'video' }

  // TikTok's documented player rather than its blockquote embed, which needs
  // their script on the page. The player is just the video; `music_info`,
  // `description` and `rel` turn off the sound credit, the caption and the
  // "more videos" shelf it would otherwise draw over a small card.
  const tt = tiktok(url)
  if(tt) return { src: `https://www.tiktok.com/player/v1/${tt}?music_info=0&description=0&rel=0`, title, aspect: 'video' }

  // Instagram's own embed page, framed directly rather than through its
  // `embed.js`. It brings Instagram's chrome with it (the account header and
  // a "View more on Instagram" footer, always on white), and none of that can
  // be restyled from here. The alternative was a link card.
  const ig = instagram(url)
  if(ig) return { src: `https://www.instagram.com/${ig.kind}/${ig.code}/embed/`, title, aspect: 'video' }

  return null
}

/**
 * A still from the video, so a block can show what it is without loading a
 * player to do it.
 *
 * YouTube's iframe draws its own furniture at a fixed size: an avatar, the
 * channel name, the title and a "Watch on YouTube" bar. In a large block that
 * is the video; in a one-by-one block it is almost all of it, and the picture
 * is the part that gets squeezed out. A poster is just the frame, so it reads
 * at any size the grid offers.
 *
 * It is also cheaper and quieter. An iframe per block loads a player per block
 * and lets the platform set cookies before anyone has asked to watch anything;
 * an image does neither until the play button is pressed.
 *
 * Returns two URLs. `maxres` does not exist for every video, which is only
 * discoverable by asking for it, so the caller falls back on error.
 */
export function videoPoster(url: string | undefined): { src: string, fallback: string, platform: 'youtube' } | null {
  if(!url) return null
  const id = youtube(url)
  if(!id) return null
  return {
    src: `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`,
    // 480x360, present for every video ever uploaded.
    fallback: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
    // Named here rather than guessed by the renderer: this function is the one
    // that knows which service the still came from, and Vimeo has no equivalent
    // still endpoint, so it falls through to the player instead.
    platform: 'youtube',
  }
}

/**
 * Where to ask Spotify for a link's cover art, or null for any other link.
 *
 * oEmbed is key-free and answers browsers directly (it sends
 * `Access-Control-Allow-Origin: *`), and it knows tracks, albums, playlists,
 * artists, episodes and shows alike. The link is rebuilt from its kind and id
 * so a locale prefix or a `?si=` share token cannot split one cover into two
 * cache entries.
 */
export function spotifyArtworkEndpoint(url: string | undefined): string | null {
  const sp = url ? spotify(url) : null
  if(!sp) return null
  return `https://open.spotify.com/oembed?url=${encodeURIComponent(`https://open.spotify.com/${sp.kind}/${sp.id}`)}`
}

export function audioEmbed(url: string | undefined, title: string, dark = true): Embed | null {
  if(!url) return null

  const sp = spotify(url)
  // Spotify's player does not follow the page it is embedded in, so a default
  // one renders as a pale slab on a dark page. `theme=0` is its dark variant.
  if(sp) {
    return {
      src: `https://open.spotify.com/embed/${sp.kind}/${sp.id}${dark ? '?theme=0' : ''}`,
      title,
      aspect: 'audio',
      layouts: SPOTIFY_LAYOUTS,
    }
  }

  // A track, a playlist or a whole profile: the widget resolves any of them.
  //
  // `visual` is the cover art run edge to edge with the controls in white over
  // it. SoundCloud has no dark theme, and its classic player is a pale grey
  // bar that reads as a hole in a dark page; it is also a fixed 166px that a
  // card's room clips through the waveform. The visual one redraws at any
  // height, so it takes no `layouts` and fills the card the way video does.
  // `buying`, `sharing` and `download` are its own buttons for leaving the page.
  if(/soundcloud\.com\//.test(url)) {
    return {
      src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}`
        + '&color=%23ff5500&visual=true&show_artwork=true&hide_related=true&show_comments=false'
        + '&show_reposts=false&show_teaser=false&buying=false&sharing=false&download=false',
      title,
      aspect: 'audio',
    }
  }

  return null
}

/**
 * A map, from coordinates rather than from a pasted link.
 *
 * Google's Maps Embed API where a key is configured, because it is the map
 * people recognise and the one whose tiles are current in Mongolia. That API is
 * free and unmetered, unlike the JavaScript one the editor uses, so putting it
 * on every public profile costs nothing.
 *
 * OpenStreetMap otherwise. Keeping the fallback means a deploy with no key set
 * still renders a map rather than a blank card, and it is what development runs
 * on before anybody has touched the Cloud console.
 */
export function mapEmbed(coords: { lat: number, lng: number } | undefined, title: string): Embed | null {
  if(!coords) return null
  const { lat, lng } = coords
  if(!Number.isFinite(lat) || !Number.isFinite(lng)) return null

  const key = process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY
  if(key) {
    // `q` is the pin and `center` is the view, and they are the same point
    // here. Zoom 16 is a street: close enough to see which building, wide
    // enough to recognise the junction it is on.
    const point = `${lat},${lng}`
    return {
      src: `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(key)}&q=${encodeURIComponent(point)}&center=${encodeURIComponent(point)}&zoom=16`,
      title,
      aspect: 'video',
    }
  }

  const span = 0.004
  const box = [lng - span, lat - span, lng + span, lat + span].map(value => value.toFixed(5)).join('%2C')
  return {
    src: `https://www.openstreetmap.org/export/embed.html?bbox=${box}&layer=mapnik&marker=${lat}%2C${lng}`,
    title,
    aspect: 'video',
  }
}
